import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Pencil, Trash2, Save, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import useApi from '../../api';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';

export interface TaskDefinition {
  id: string;
  agent_id: string;
  name: string;
  system_name: string;
  instruction: string;
  created_at: string | null;
  updated_at: string | null;
  usage_count?: number;
}

interface TaskDefinitionEditorProps {
  taskDef: TaskDefinition;
  agentId: string;
  onUpdated: (updated: TaskDefinition) => void;
  onDeleted: (id: string) => void;
}

export default function TaskDefinitionEditor({
  taskDef,
  agentId,
  onUpdated,
  onDeleted,
}: TaskDefinitionEditorProps) {
  const { put, remove } = useApi();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [name, setName] = useState(taskDef.name);
  const [systemName, setSystemName] = useState(taskDef.system_name);
  const [instruction, setInstruction] = useState(taskDef.instruction);

  const stepCount = taskDef.instruction.split('\n').filter((l) => l.trim()).length;
  const usageCount = taskDef.usage_count ?? 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await put(`/agents/${agentId}/task-definitions/${taskDef.id}`, {
        name: name.trim(),
        system_name: systemName.trim(),
        instruction: instruction.trim(),
      });
      onUpdated(res.data);
      setEditing(false);
      toast.success('Task definition saved', toastConfig);
    } catch (err) {
      console.error('Error saving task definition:', err);
      toast.error('Failed to save task definition', toastConfig);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this task definition from this agent?')) return;
    setDeleting(true);
    try {
      await remove(`/agents/${agentId}/task-definitions/${taskDef.id}`);
      onDeleted(taskDef.id);
      toast.success('Task definition removed from agent', toastConfig);
    } catch (err) {
      console.error('Error deleting task definition:', err);
      toast.error('Failed to remove task definition from agent', toastConfig);
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setName(taskDef.name);
    setSystemName(taskDef.system_name);
    setInstruction(taskDef.instruction);
    setEditing(false);
  };

  return (
    <div className={cn(
      'overflow-hidden rounded-lg border bg-card transition-colors hover:border-foreground/20',
      expanded && 'border-foreground/20'
    )}>
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-muted/30">
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
              <h4 className="truncate text-sm font-medium">{taskDef.name}</h4>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {stepCount} step{stepCount !== 1 ? 's' : ''}
                <span className="mx-1.5 text-muted-foreground/40">·</span>
                <span className="font-mono">{taskDef.system_name}</span>
              </p>
          </div>
          <div className="ml-2 flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Badge variant="outline" className="mr-1 shrink-0 px-1.5 py-0 text-[10px] font-medium text-muted-foreground">
              {usageCount} session{usageCount !== 1 ? 's' : ''}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setExpanded(true);
                setEditing(true);
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="space-y-4 border-t p-4">
            {editing ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor={`td-name-${taskDef.id}`}>Name</Label>
                  <Input
                    id={`td-name-${taskDef.id}`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Create New Project"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`td-sysname-${taskDef.id}`}>System Name</Label>
                  <Input
                    id={`td-sysname-${taskDef.id}`}
                    value={systemName}
                    onChange={(e) => setSystemName(e.target.value)}
                    placeholder="e.g. create_new_project"
                    className="font-mono text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`td-instruction-${taskDef.id}`}>Instructions</Label>
                  <Textarea
                    id={`td-instruction-${taskDef.id}`}
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    rows={10}
                    className="font-mono text-sm"
                    placeholder="Step-by-step instructions..."
                  />
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving}>
                    <X className="h-3.5 w-3.5 mr-1" />
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    <Save className="h-3.5 w-3.5 mr-1" />
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </>
            ) : (
              <div>
                <div>
                  <span className="text-xs font-medium text-muted-foreground">Instructions</span>
                  <div className="mt-2 whitespace-pre-wrap text-sm leading-6 text-foreground/90">
                    {taskDef.instruction}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
